// <ThemeWorldAtmosphere> — a world's scoped background layers.
//
// Renders absolutely-positioned, pointer-events:none layers inside whatever
// position:relative parent it lives in (the slide root). Each world supplies a
// layer set + a scoped stylesheet, namespaced under a wrapper class (`.tw-neon`,
// `.tw-obsidian`) and injected ONCE, ref-counted, so multiple instances share one
// <style> tag and nothing leaks globally.
//
// GPU-friendly (transform/opacity only). Reduced-motion is handled inside each
// world's CSS via a `@media (prefers-reduced-motion: reduce)` block that calms
// the layers (animations off, motes/grain hidden) — matching the design mockups.

import { useEffect } from 'react';
import type { ThemeId } from '../../components/course-builder/slides/slideTypes';

// ─────────────────────────────────────────────────────────────────────────────
// Per-world atmosphere definition: a wrapper class, the layer markup, and CSS.
// ─────────────────────────────────────────────────────────────────────────────

interface AtmosphereWorld {
  /** namespace class, e.g. 'tw-neon' */
  wrapper: string;
  /** stable id for the injected <style> tag */
  styleId: string;
  /** layer <div> class names rendered inside the wrapper, back-to-front */
  layers: string[];
  /** scoped CSS, all rules under `.${wrapper}` */
  css: (colors: WorldColors) => string;
}

interface WorldColors {
  primary: string;
  accent: string;
  background: string;
}

// ── Neon Oracle: perspective grid drift + scanlines + vignette + grain ────────

const neon: AtmosphereWorld = {
  wrapper: 'tw-neon',
  styleId: 'tw-neon-atmos-styles',
  layers: ['tw-neon-grid', 'tw-neon-roll', 'tw-neon-scanlines', 'tw-neon-vignette', 'tw-neon-grain', 'tw-neon-bloom'],
  css: ({ primary, accent }) => `
.tw-neon { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.tw-neon .tw-neon-grid {
  position: absolute; inset: -20%;
  background-image:
    linear-gradient(${accent}0b 1px, transparent 1px),
    linear-gradient(90deg, ${accent}0b 1px, transparent 1px);
  background-size: 48px 48px;
  transform: perspective(700px) rotateX(64deg);
  transform-origin: center top;
  animation: twNeonGridDrift 14s linear infinite;
  opacity: 0.5;
}
@keyframes twNeonGridDrift { from { background-position: 0 0; } to { background-position: 0 48px; } }
.tw-neon .tw-neon-roll {
  position: absolute; left: 0; right: 0; height: 30%;
  background: linear-gradient(180deg, transparent, ${accent}0a 48%, ${primary}08 55%, transparent);
  animation: twNeonRollDown 11s linear infinite;
  mix-blend-mode: screen;
}
@keyframes twNeonRollDown { from { top: -25%; } to { top: 125%; } }
.tw-neon .tw-neon-scanlines {
  position: absolute; inset: 0;
  background: repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.06) 3px);
  mix-blend-mode: multiply; opacity: 0.4;
}
.tw-neon .tw-neon-vignette {
  position: absolute; inset: 0;
  background: radial-gradient(120% 90% at 50% 42%, transparent 38%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.85) 100%);
  mix-blend-mode: multiply;
}
.tw-neon .tw-neon-grain {
  position: absolute; inset: 0; opacity: 0.028;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation: twNeonGrainShift 6s ease-in-out infinite;
}
@keyframes twNeonGrainShift { 0% { transform: translate(0,0); } 50% { transform: translate(-2px,1px); } 100% { transform: translate(0,0); } }
.tw-neon .tw-neon-bloom {
  position: absolute; inset: 0;
  background: radial-gradient(120% 90% at 50% 40%, ${accent}05, transparent 70%);
  animation: twNeonBloomBreath 9s ease-in-out infinite;
}
@keyframes twNeonBloomBreath { 0%,100% { opacity: 0.5; } 50% { opacity: 0.85; } }
@media (prefers-reduced-motion: reduce) {
  .tw-neon .tw-neon-grid, .tw-neon .tw-neon-roll, .tw-neon .tw-neon-grain, .tw-neon .tw-neon-bloom { animation: none !important; }
}
`,
};

// ── Obsidian: warm desk-lamp pool + vignette + film grain + drifting dust ─────

const obsidian: AtmosphereWorld = {
  wrapper: 'tw-obsidian',
  styleId: 'tw-obsidian-atmos-styles',
  layers: ['tw-obs-lamp', 'tw-obs-motes', 'tw-obs-vignette', 'tw-obs-grain'],
  css: ({ primary, accent }) => `
.tw-obsidian { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.tw-obsidian .tw-obs-lamp {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 50% 42% at 50% 40%,
     ${primary}38 0%, ${accent}1a 30%, transparent 62%);
  mix-blend-mode: screen;
  animation: twObsFlicker 7s ease-in-out infinite;
}
@keyframes twObsFlicker { 0%,100% { opacity: 1; } 47% { opacity: .93; } 50% { opacity: .82; } 53% { opacity: .95; } }
.tw-obsidian .tw-obs-vignette {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 75% 70% at 50% 42%,
    rgba(0,0,0,0) 40%, rgba(0,0,0,.55) 78%, rgba(0,0,0,.92) 100%);
}
.tw-obsidian .tw-obs-grain {
  position: absolute; inset: -50%; width: 200%; height: 200%; opacity: .07;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation: twObsGrainShift .6s steps(4) infinite;
}
@keyframes twObsGrainShift {
  0% { transform: translate(0,0); } 25% { transform: translate(-3%,2%); }
  50% { transform: translate(2%,-3%); } 75% { transform: translate(-2%,-2%); } 100% { transform: translate(3%,3%); }
}
.tw-obsidian .tw-obs-motes { position: absolute; inset: 0; overflow: hidden; }
.tw-obsidian .tw-obs-mote {
  position: absolute; width: 3px; height: 3px; border-radius: 50%;
  background: radial-gradient(circle, rgba(232,226,214,.9), rgba(232,226,214,0));
  opacity: 0; animation: twObsDrift linear infinite;
}
@keyframes twObsDrift {
  0% { transform: translate(0,0) scale(.6); opacity: 0; }
  10% { opacity: .7; } 90% { opacity: .5; }
  100% { transform: translate(var(--tw-dx), var(--tw-dy)) scale(1.1); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .tw-obsidian .tw-obs-lamp, .tw-obsidian .tw-obs-grain { animation: none !important; }
  .tw-obsidian .tw-obs-motes { display: none; }
}
`,
};

// ── Aurora Drift: slow-drifting aurora gradient mesh + bokeh + star/grain ─────
// Light/airy register. Blurred radial blobs (teal/green/rose/violet over night)
// drift on long alternating loops; soft bokeh light particles rise; a faint
// twinkling star field sits behind. Calm under reduced motion.

const aurora: AtmosphereWorld = {
  wrapper: 'tw-aurora',
  styleId: 'tw-aurora-atmos-styles',
  layers: [
    'tw-aurora-stage',
    'tw-aurora-stars',
    'tw-aurora-bokeh',
  ],
  // primary = teal/green glow, accent = violet; mesh also mixes a fixed rose.
  css: ({ primary, accent }) => `
.tw-aurora { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }

/* gradient mesh — blurred drifting blobs over a deep-night base */
.tw-aurora .tw-aurora-stage {
  position: absolute; inset: -20%;
  background:
    radial-gradient(ellipse 120% 80% at 50% -10%, #131a3c 0%, transparent 60%);
}
.tw-aurora .tw-aurora-blob {
  position: absolute; border-radius: 50%;
  filter: blur(70px); opacity: 0.5; mix-blend-mode: screen; will-change: transform;
}
.tw-aurora .tw-aurora-b1 {
  width: 52vmax; height: 52vmax; top: 2%; left: 8%;
  background: radial-gradient(circle at 40% 40%, ${primary}, transparent 68%);
  animation: twAuroraDrift1 34s cubic-bezier(0.4,0,0.1,1) infinite alternate;
}
.tw-aurora .tw-aurora-b2 {
  width: 46vmax; height: 46vmax; top: 30%; left: 45%;
  background: radial-gradient(circle at 50% 50%, #5ef2a0, transparent 66%);
  animation: twAuroraDrift2 41s cubic-bezier(0.4,0,0.1,1) infinite alternate;
}
.tw-aurora .tw-aurora-b3 {
  width: 44vmax; height: 44vmax; top: 12%; left: 58%;
  background: radial-gradient(circle at 50% 50%, ${accent}, transparent 66%);
  animation: twAuroraDrift3 38s cubic-bezier(0.4,0,0.1,1) infinite alternate;
}
.tw-aurora .tw-aurora-b4 {
  width: 38vmax; height: 38vmax; top: 50%; left: 20%;
  background: radial-gradient(circle at 50% 50%, #ff8fb1, transparent 70%);
  animation: twAuroraDrift4 47s cubic-bezier(0.4,0,0.1,1) infinite alternate;
}
@keyframes twAuroraDrift1 { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(14vmax,10vmax) scale(1.18); } }
@keyframes twAuroraDrift2 { 0% { transform: translate(0,0) scale(1.1); } 100% { transform: translate(-16vmax,8vmax) scale(0.92); } }
@keyframes twAuroraDrift3 { 0% { transform: translate(0,0) scale(0.95); } 100% { transform: translate(-10vmax,14vmax) scale(1.22); } }
@keyframes twAuroraDrift4 { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(12vmax,-10vmax) scale(1.15); } }

/* faint star / grain field */
.tw-aurora .tw-aurora-stars {
  position: absolute; inset: 0; opacity: 0.7;
  background-image:
    radial-gradient(1px 1px at 20% 30%, rgba(234,244,255,0.8), transparent),
    radial-gradient(1px 1px at 75% 15%, rgba(234,244,255,0.5), transparent),
    radial-gradient(1.5px 1.5px at 45% 70%, rgba(234,244,255,0.6), transparent),
    radial-gradient(1px 1px at 88% 55%, rgba(234,244,255,0.45), transparent),
    radial-gradient(1px 1px at 12% 80%, rgba(234,244,255,0.55), transparent),
    radial-gradient(1px 1px at 60% 88%, rgba(234,244,255,0.4), transparent),
    radial-gradient(1px 1px at 33% 12%, rgba(234,244,255,0.5), transparent);
  animation: twAuroraTwinkle 8s ease-in-out infinite alternate;
}
@keyframes twAuroraTwinkle { 0% { opacity: 0.45; } 100% { opacity: 0.8; } }

/* drifting bokeh light particles */
.tw-aurora .tw-aurora-bokeh { position: absolute; inset: 0; overflow: hidden; }
.tw-aurora .tw-aurora-mote {
  position: absolute; bottom: -10%; border-radius: 50%;
  filter: blur(1px); opacity: 0;
  animation: twAuroraRise linear infinite;
}
@keyframes twAuroraRise {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 0.7; } 90% { opacity: 0.5; }
  100% { transform: translateY(-120vh) translateX(6vw); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .tw-aurora .tw-aurora-blob { animation: none !important; }
  .tw-aurora .tw-aurora-stars { animation: none !important; opacity: 0.6; }
  .tw-aurora .tw-aurora-bokeh { display: none; }
}
`,
};

// ── Terra: dappled sunlight drift + paper grain + rising pollen motes ─────────
// Light/airy greenhouse register. Warm honey + cool sky glows wash the corners; a
// soft set of dappled light pools drifts slowly; a faint paper grain sits over it
// (multiply); honey-lit pollen motes rise. Calm under reduced motion.

const terra: AtmosphereWorld = {
  wrapper: 'tw-terra',
  styleId: 'tw-terra-atmos-styles',
  layers: ['tw-terra-wash', 'tw-terra-dapple', 'tw-terra-grain', 'tw-terra-pollen'],
  // primary = leaf green, accent = terracotta; mesh also uses fixed honey + sky.
  css: ({ primary, accent }) => `
.tw-terra { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }

/* corner washes — warm honey (top-right) + cool sky (top-left) */
.tw-terra .tw-terra-wash {
  position: absolute; inset: 0;
  background:
    radial-gradient(120% 90% at 82% -8%, rgba(230,168,46,0.40) 0%, rgba(230,168,46,0) 46%),
    radial-gradient(90% 70% at 10% 6%, rgba(191,227,224,0.50) 0%, rgba(191,227,224,0) 52%),
    radial-gradient(80% 60% at 50% 112%, ${primary}1f 0%, transparent 58%);
}

/* dappled light pools, slowly drifting */
.tw-terra .tw-terra-dapple {
  position: absolute; inset: -20%;
  background:
    radial-gradient(28% 22% at 20% 24%, rgba(255,247,214,0.50), transparent 60%),
    radial-gradient(22% 18% at 72% 60%, rgba(255,247,214,0.40), transparent 60%),
    radial-gradient(30% 24% at 86% 18%, rgba(255,247,214,0.42), transparent 60%),
    radial-gradient(24% 20% at 38% 78%, ${accent}14, transparent 62%);
  animation: twTerraDrift 26s ease-in-out infinite alternate;
}
@keyframes twTerraDrift {
  from { transform: translate(-1.5%, -1%) scale(1); }
  to   { transform: translate(2.5%, 2%) scale(1.06); }
}

/* paper grain */
.tw-terra .tw-terra-grain {
  position: absolute; inset: 0; opacity: 0.5; mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.22'/%3E%3C/svg%3E");
}

/* drifting pollen */
.tw-terra .tw-terra-pollen { position: absolute; inset: 0; overflow: hidden; }
.tw-terra .tw-terra-mote {
  position: absolute; bottom: -12px; width: 7px; height: 7px; border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff6cf, #e6a82e);
  box-shadow: 0 0 9px 2px rgba(230,168,46,0.5);
  opacity: 0; animation: twTerraRise linear infinite;
}
@keyframes twTerraRise {
  0% { transform: translateY(0) translateX(0) scale(0.6); opacity: 0; }
  12% { opacity: 0.85; } 80% { opacity: 0.7; }
  100% { transform: translateY(-104vh) translateX(var(--tw-sway, 30px)) scale(1.1); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .tw-terra .tw-terra-dapple { animation: none !important; }
  .tw-terra .tw-terra-pollen { display: none; }
}
`,
};

// ── Hyperpop Arcade: drifting candy glows + panning dot grid + bobbing stickers ─
// Neo-brutalist arcade register. Big soft candy radial glows drift over a cream
// base; a black dot grid pans slowly; chunky geometric stickers bob at the edges.
// Calm under reduced motion.

const hyperpop: AtmosphereWorld = {
  wrapper: 'tw-hyperpop',
  styleId: 'tw-hyperpop-atmos-styles',
  layers: ['tw-hp-candy', 'tw-hp-dots', 'tw-hp-stickers'],
  // primary = blue, accent = pink; candy mix also uses fixed yellow/lime/tangerine.
  css: ({ primary, accent }) => `
.tw-hyperpop { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }

.tw-hyperpop .tw-hp-candy {
  position: absolute; inset: 0;
  background:
    radial-gradient(circle at 15% 20%, ${accent}2e, transparent 40%),
    radial-gradient(circle at 85% 15%, ${primary}29, transparent 42%),
    radial-gradient(circle at 75% 85%, rgba(61,220,132,0.18), transparent 40%),
    radial-gradient(circle at 20% 80%, rgba(255,107,26,0.16), transparent 40%);
  animation: twHpDrift 18s ease-in-out infinite alternate;
}
@keyframes twHpDrift {
  0% { background-position: 0 0, 0 0, 0 0, 0 0; }
  100% { background-position: 60px 40px, -50px 30px, 40px -40px, -30px -50px; }
}

.tw-hyperpop .tw-hp-dots {
  position: absolute; inset: -40px;
  background-image: radial-gradient(#0a0a0a 1.5px, transparent 1.6px);
  background-size: 26px 26px; opacity: 0.06;
  animation: twHpDotPan 30s linear infinite;
}
@keyframes twHpDotPan { from { background-position: 0 0; } to { background-position: 26px 26px; } }

.tw-hyperpop .tw-hp-stickers { position: absolute; inset: 0; }
.tw-hyperpop .tw-hp-sticker { position: absolute; animation: twHpBob 5s ease-in-out infinite; }
@keyframes twHpBob {
  0%, 100% { transform: translateY(0) rotate(var(--tw-r, 0deg)); }
  50% { transform: translateY(-14px) rotate(calc(var(--tw-r, 0deg) + 5deg)); }
}

@media (prefers-reduced-motion: reduce) {
  .tw-hyperpop .tw-hp-candy, .tw-hyperpop .tw-hp-dots { animation: none !important; }
  .tw-hyperpop .tw-hp-sticker { animation: none !important; }
}
`,
};

// ── Risograph Assembly: halftone dot field + paper grain + soft vignette ──────
// Print/zine register. Everything STATIC (no animation — print doesn't move): a
// fine halftone dot grid, a multiply paper-noise grain, and a gentle vignette.

const risograph: AtmosphereWorld = {
  wrapper: 'tw-riso',
  styleId: 'tw-riso-atmos-styles',
  layers: ['tw-riso-halftone', 'tw-riso-grain', 'tw-riso-vignette'],
  css: () => `
.tw-riso { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.tw-riso .tw-riso-halftone {
  position: absolute; inset: 0;
  background-image: radial-gradient(#16130d 0.8px, transparent 0.9px);
  background-size: 7px 7px; opacity: 0.06;
}
.tw-riso .tw-riso-grain {
  position: absolute; inset: 0; opacity: 0.5; mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.22'/%3E%3C/svg%3E");
}
.tw-riso .tw-riso-vignette {
  position: absolute; inset: 0;
  background: radial-gradient(120% 120% at 50% 35%, transparent 55%, rgba(22,19,13,0.10) 100%);
}
`,
};

const WORLDS: Partial<Record<ThemeId, AtmosphereWorld>> = {
  'neon-oracle': neon,
  obsidian,
  aurora,
  terra,
  hyperpop,
  risograph,
};

// ─────────────────────────────────────────────────────────────────────────────
// Ref-counted scoped <style> injection (one tag per world).
// ─────────────────────────────────────────────────────────────────────────────

function useScopedAtmosphereStyles(world: AtmosphereWorld, colors: WorldColors): void {
  // Token values are stable per theme; intentionally run once on mount.
  useEffect(() => {
    let tag = document.getElementById(world.styleId) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = world.styleId;
      tag.dataset.refcount = '0';
      tag.textContent = world.css(colors);
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(world.styleId) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function ThemeWorldAtmosphere({
  worldId,
  primary,
  accent,
  background,
  motesCount = 26,
  style,
}: {
  worldId: ThemeId;
  /** world palette — defaults pulled from the theme by the consumer */
  primary: string;
  accent: string;
  background: string;
  /** obsidian only — number of drifting dust motes */
  motesCount?: number;
  style?: React.CSSProperties;
}) {
  const world = WORLDS[worldId];
  // Hook must run unconditionally; fall back to neon when a world isn't registered.
  const resolved = world ?? neon;
  useScopedAtmosphereStyles(resolved, { primary, accent, background });

  if (!world) return null;

  return (
    <div className={world.wrapper} aria-hidden style={style}>
      {world.layers.map((layer) =>
        layer === 'tw-aurora-stage' ? (
          <div key={layer} className={layer}>
            <div className="tw-aurora-blob tw-aurora-b1" />
            <div className="tw-aurora-blob tw-aurora-b2" />
            <div className="tw-aurora-blob tw-aurora-b3" />
            <div className="tw-aurora-blob tw-aurora-b4" />
          </div>
        ) : layer === 'tw-aurora-bokeh' ? (
          <div key={layer} className={layer}>
            {Array.from({ length: 14 }).map((_, i) => {
              const tints = [
                'rgba(94,242,160,0.5)',
                'rgba(21,196,184,0.5)',
                'rgba(255,143,177,0.45)',
                'rgba(139,108,255,0.45)',
              ];
              const size = 6 + Math.random() * 26;
              const moteStyle: React.CSSProperties = {
                width: size,
                height: size,
                left: `${Math.random() * 100}%`,
                animationDuration: `${16 + Math.random() * 20}s`,
                animationDelay: `${-Math.random() * 24}s`,
                background: `radial-gradient(circle, ${tints[i % tints.length]}, rgba(0,0,0,0) 70%)`,
              };
              return <div key={i} className="tw-aurora-mote" style={moteStyle} />;
            })}
          </div>
        ) : layer === 'tw-hp-stickers' ? (
          <div key={layer} className={layer}>
            {[
              { top: '12%', left: '5%', r: '-8deg', d: '0s', svg: 'star', fill: '#ffe600' },
              { top: '70%', left: '4%', r: '10deg', d: '0.8s', svg: 'blob', fill: '#ff3d9a' },
              { top: '18%', right: '6%', r: '12deg', d: '1.6s', svg: 'eye', fill: '#2d5cff' },
              { top: '76%', right: '6%', r: '-10deg', d: '0.4s', svg: 'diamond', fill: '#3ddc84' },
              { top: '46%', left: '2%', r: '6deg', d: '1.2s', svg: 'star', fill: '#ff6b1a' },
            ].map((s, i) => {
              const style: React.CSSProperties = {
                top: s.top,
                left: s.left,
                right: s.right,
                animationDelay: s.d,
                ['--tw-r' as string]: s.r,
              };
              return (
                <div key={i} className="tw-hp-sticker" style={style}>
                  <svg width={60} height={60} viewBox="0 0 64 64">
                    {s.svg === 'star' && (
                      <path d="M32 5l7 19h20l-16 12 6 20-17-12-17 12 6-20L5 24h20z" fill={s.fill} stroke="#0a0a0a" strokeWidth={4} strokeLinejoin="round" />
                    )}
                    {s.svg === 'blob' && (
                      <path d="M32 6c10 0 8 12 18 14s10 14 0 18-8 16-18 14-8-12-18-14S4 38 14 34s8-16 18-28z" fill={s.fill} stroke="#0a0a0a" strokeWidth={4} />
                    )}
                    {s.svg === 'eye' && (
                      <>
                        <circle cx={32} cy={32} r={26} fill={s.fill} stroke="#0a0a0a" strokeWidth={4} />
                        <circle cx={32} cy={32} r={11} fill="#fdfcf7" stroke="#0a0a0a" strokeWidth={3} />
                      </>
                    )}
                    {s.svg === 'diamond' && (
                      <path d="M32 5l27 27-27 27L5 32z" fill={s.fill} stroke="#0a0a0a" strokeWidth={4} strokeLinejoin="round" />
                    )}
                  </svg>
                </div>
              );
            })}
          </div>
        ) : layer === 'tw-terra-pollen' ? (
          <div key={layer} className={layer}>
            {Array.from({ length: 24 }).map((_, i) => {
              const dur = 11 + Math.random() * 14;
              const sc = 0.4 + Math.random() * 0.9;
              const moteStyle: React.CSSProperties = {
                left: `${Math.random() * 100}%`,
                animationDuration: `${dur}s`,
                animationDelay: `${-Math.random() * dur}s`,
                ['--tw-sway' as string]: `${Math.random() * 120 - 60}px`,
                transform: `scale(${sc})`,
              };
              return <div key={i} className="tw-terra-mote" style={moteStyle} />;
            })}
          </div>
        ) : layer === 'tw-obs-motes' ? (
          <div key={layer} className={layer}>
            {Array.from({ length: motesCount }).map((_, i) => {
              const dx = `${Math.random() * 60 - 30}px`;
              const dy = `${-80 - Math.random() * 120}px`;
              const moteStyle: React.CSSProperties = {
                left: `${Math.random() * 100}%`,
                top: `${40 + Math.random() * 40}%`,
                animationDuration: `${9 + Math.random() * 12}s`,
                animationDelay: `${-Math.random() * 16}s`,
                // CSS custom props for the drift endpoint
                ['--tw-dx' as string]: dx,
                ['--tw-dy' as string]: dy,
              };
              return <div key={i} className="tw-obs-mote" style={moteStyle} />;
            })}
          </div>
        ) : (
          <div key={layer} className={layer} />
        ),
      )}
    </div>
  );
}
